import styled from '@emotion/styled'
import SVG from '../../assets/svg'
import { CircularProgress } from '@mui/material'
import { color, font, size } from '../../styles'

interface RoleInfoForAvatar {
   displayName: string
   avatarImage: string | null | undefined
}

interface Props {
   roles: RoleInfoForAvatar[]
   maxAvatarsToShow?: number
   className?: string
   onClick?: () => void
   showEditAvatar?: boolean
   isAvatarsGroup?: boolean
   loading?: boolean
}

const Avatar: React.FC<Props> = (props) => {
   const { roles, maxAvatarsToShow, className, isAvatarsGroup, showEditAvatar, loading, onClick } =
      props
   const isSingleAvatar = !isAvatarsGroup && roles.length === 1
   const maxAvatars = maxAvatarsToShow && maxAvatarsToShow <= roles.length ? maxAvatarsToShow : 4
   const remainingAvatars = roles.length - maxAvatars
   const onlyRemainingRoleAfterMax = remainingAvatars === 1 ? roles[maxAvatars] : null

   const RemainingAvatar = () => {
      if (remainingAvatars > 1) {
         return <AvatarPlaceholder remainingAvatars={remainingAvatars} />
      }

      if (onlyRemainingRoleAfterMax) {
         if (onlyRemainingRoleAfterMax.avatarImage) {
            return <ImageAvatar {...onlyRemainingRoleAfterMax} />
         } else {
            return <InitialsAvatar {...onlyRemainingRoleAfterMax} />
         }
      }

      return null
   }
   return (
      <Holder className={className || ''} onClick={onClick}>
         {isSingleAvatar && (
            <div className={`single-avatar-holder ${loading ? 'loading' : ''}`}>
               {roles[0].avatarImage ? (
                  <ImageAvatar {...roles[0]} />
               ) : (
                  <InitialsAvatar {...roles[0]} />
               )}
               {loading && <CircularProgress size={'105%'} className="loading-spinner" />}
               {showEditAvatar && (
                  <div className="edit-avatar">
                     <SVG.form.edit className="icon" />
                  </div>
               )}
            </div>
         )}
         {!isSingleAvatar && (
            <div className="avatars-group-holder">
               {roles?.map((role, index) => {
                  if (index <= maxAvatars - 1) {
                     if (role.avatarImage) {
                        return <ImageAvatar key={index} {...role} />
                     } else {
                        return <InitialsAvatar key={index} {...role} />
                     }
                  }
               })}
               {remainingAvatars > 0 && <RemainingAvatar />}
            </div>
         )}
      </Holder>
   )
}

// ==============
// InitialsAvatar
// ==============
const InitialsAvatar: React.FC<RoleInfoForAvatar> = (props) => {
   const { displayName } = props
   const getInitials = (name: string) => {
      const names = name?.split(' ') ?? ['']
      let initials = names[0].substring(0, 1).toUpperCase()
      if (names.length > 1) {
         initials += names[names.length - 1].substring(0, 1).toUpperCase()
      }
      return initials.toUpperCase()
   }
   return (
      <div className="avatar-item initials">
         <span className="content">{getInitials(displayName)}</span>
      </div>
   )
}

// ===========
// ImageAvatar
// ===========
const ImageAvatar: React.FC<RoleInfoForAvatar> = (props) => {
   const { avatarImage: avatarURL, displayName } = props
   return (
      <img className="avatar-item image" src={avatarURL as string} alt={displayName + '-avatar'} />
   )
}

// =================
// AvatarPlaceholder
// =================
const AvatarPlaceholder: React.FC<{ remainingAvatars: number }> = ({ remainingAvatars }) => {
   return (
      <div className="avatar-item placeholder">
         <span className="content">{`+${remainingAvatars}`}</span>
      </div>
   )
}

const Holder = styled.div`
   .single-avatar-holder {
      cursor: pointer;
      position: relative;

      .avatar-item {
         width: 70px;
         height: 70px;
         border-radius: 50%;
         display: flex;
         align-items: center;
         justify-content: center;
         overflow: hidden;
         border: 3px solid var(--active-role-color, #000);
         box-sizing: border-box;
         padding: 3px;
         user-select: none;
         pointer-events: none;

         &.initials {
            .content {
               background-color: ${color.grayDark};
               width: 100%;
               height: 100%;
               border-radius: 50%;
               display: flex;
               align-items: center;
               justify-content: center;

               font-size: ${size.l};
               ${font.regular};
               color: ${color.white};
            }
         }

         &.image {
            img {
               width: 100%;
               height: 100%;
               object-fit: cover;
               box-sizing: content-box;
            }
         }
      }

      .loading-spinner {
         position: absolute;
         top: -2px;
         left: -2px;
         z-index: 1;

         .MuiCircularProgress-circle {
            stroke: var(--active-role-color, ${color.green});
            stroke-width: 1.6px;
         }
      }

      .edit-avatar {
         position: absolute;
         bottom: 0;
         right: 0;
         width: 1.9em;
         height: 1.9em;
         border-radius: 50%;
         overflow: hidden;
         border: 3px solid var(--holder-background-color, #fff);
         background-color: var(--active-role-color);
         display: flex;
         align-items: center;
         justify-content: center;
         transition: all 0.3s ease-in-out;
         box-sizing: border-box;

         .icon {
            width: 0.9em;
            height: 0.9em;
            fill: ${color.white};
            transition: all 0.3s ease-in-out;
         }
      }

      &:hover {
         .edit-avatar {
            width: 90%;
            height: 90%;
            bottom: 5%;
            right: 5%;
            /* background-color: color-mix(in srgb, var(--active-role-color), #ffffff27 30%); */

            .icon {
               width: 1.3em;
               height: 1.3em;
            }
         }
      }

      /* Animation for loading */
      @keyframes pulse {
         0% {
            transform: scale(0.97);
            opacity: 0.7;
         }
         50% {
            transform: scale(1);
            opacity: 1;
         }
         100% {
            transform: scale(0.97);
            opacity: 0.7;
         }
      }

      &.loading {
         animation: pulse 1.5s infinite;

         .edit-avatar {
            display: none;
         }

         .avatar-item {
            border-color: transparent;
         }
      }
   }

   .avatars-group-holder {
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      cursor: pointer;

      .avatar-item {
         position: relative;
         width: 2.5rem;
         height: 2.5rem;
         border-radius: 50%;
         object-fit: cover;
         /* box-sizing: content-box; */
         border: 4px solid var(--holder-background-color, #fff);
         user-select: none;
         pointer-events: none;
         display: flex;
         align-items: center;
         justify-content: center;

         /* not the first element */
         &:not(:first-child) {
            margin-left: -1rem;
         }

         &.initials,
         &.placeholder {
            background-color: ${color.grayDark};
            .content {
               border-radius: 50%;
               position: relative;
               top: 1px;
               margin: 0;
               padding: 0;
               line-height: 0;
               font-size: ${size.m};
               ${font.regular};
               color: ${color.white};
            }
         }

         &.image {
            img {
               width: 100%;
               height: 100%;
               object-fit: cover;
               box-sizing: content-box;
            }
         }
      }
   }
`
export type { Props as AvatarProps }
export default Avatar
